import * as React from 'react'
import { Sequence, SequenceColumnConfig, SequenceColumnProperty, SequenceColumns, SequenceProperty, ResourceRendererOptions, ResourceRendererResult } from '../../types'
import { ResourceTableHeader } from './ResourceTable'
import ResourceTableRow from './ResourceTableRow'
import ResourceTableRowData from './ResourceTableRowData'
import ResourceTableRowActions from './ResourceTableRowActions'
import { IActionListItem } from '../ActionList/ActionList'
import i18n from '../../translations'
import LocalStorage, { LocalStorageKey } from '../../LocalStorage'
import Switch from '../Form/Switch'
import styled from 'styled-components'

const ActiveContainer = styled.div`
  display: flex;
  justify-content: right;
`

export const DEFAULT_SEQUENCE_COLUMNS: SequenceColumnProperty[] = [
  'name',
  'active',
  'business_days_only'
]

const SequenceColumnConfigs: Record<SequenceColumnProperty, SequenceColumnConfig> = {
  name: {
    header: () => ({ title: i18n.t('Sequence::name') }),
    render: ({ sequence }) => sequence.name ? <b>{sequence.name}</b> : '-',
  },
  active: {
    header: () => ({ title: i18n.t('Sequence::active'), align: 'right' }),
    render: ({ sequence, onActiveToggle }) => (
      <ActiveContainer>
        <Switch
          name='enabled'
          checked={sequence.active}
          onClick={(e) => onActiveToggle && onActiveToggle(e, sequence)}
        />
      </ActiveContainer>
    ),
  },
  business_days_only: {
    header: () => ({ title: i18n.t('Sequence::business_days_only'), align: 'right' }),
    render: ({ sequence, onBusinessDayToggle }) => (
      <ActiveContainer>
        <Switch
          name='enabled'
          checked={sequence.business_days_only}
          onClick={(e) => onBusinessDayToggle && onBusinessDayToggle(e, sequence)}
        />
      </ActiveContainer>
    ),
  },
}

export interface SequenceRendererOptions extends ResourceRendererOptions<Sequence> {
  onActiveToggle?: (e: React.MouseEvent, sequence: Sequence) => void
  onBusinessDayToggle?: (e: React.MouseEvent, sequence: Sequence) => void
}

export function getSequenceTableRenderer(options: SequenceRendererOptions): ResourceRendererResult<Sequence, SequenceProperty> {
  const {
    onRowClick,
    selectedItems = [],
    onSelectionChange,
    onActionClick,
  } = options

  const columns: { label: string, value: string }[] = SequenceColumns.map(column => ({ label: i18n.t(`Sequence::${column}`), value: column }))
  const selectedColumns: SequenceProperty[] = LocalStorage
    .get(LocalStorageKey.SEQUENCE_COLUMNS, DEFAULT_SEQUENCE_COLUMNS)
    .filter((column) => SequenceColumnConfigs[column])

  const headers: ResourceTableHeader[] = selectedColumns
    .map(column => ({
      ...SequenceColumnConfigs[column].header(),
      visible: true
    }))

  headers.push({ title: '', stickyRight: '0px' })

  const renderRow = (sequence: Sequence, _index: number): React.ReactElement => {
    const isSelected = selectedItems.includes(sequence.id)
    let actions: IActionListItem[] = [
      { key: 'edit', icon: 'edit-solid', content: i18n.t('Sequences::Edit') },
      { key: 'delete', icon: 'trash-alt-solid', content: i18n.t('Sequences::Delete'), destructive: true },
    ]

    const applyMaxWidthColumns: SequenceProperty[] = [
      'name',
    ]

    let cells: React.ReactNode[] = selectedColumns.map((column) => {
      return (
        <ResourceTableRowData
          key={`${sequence.id}-${column}`}
          onClick={onRowClick ? () => onRowClick(sequence) : undefined}
          maxWidth={applyMaxWidthColumns.includes(column) ? '300px' : undefined}
          ellipse={applyMaxWidthColumns.includes(column)}
          textAlign={SequenceColumnConfigs[column].header().align}
        >
          {SequenceColumnConfigs[column].render({ sequence, ...options })}
        </ResourceTableRowData>
      )
    })

    // Add actions cell if actions are provided
    if (actions?.length > 0 && onActionClick) {
      cells = [
        ...cells,
        <ResourceTableRowActions
          key={`${sequence.id}-actions`}
          actions={actions}
          onActionClick={(key) => onActionClick(key, sequence)}
          sticky={true}
          stickyRight='0px'
        />
      ]
    }

    return (
      <ResourceTableRow
        key={sequence.id}
        selected={isSelected}
        onSelectionChange={onSelectionChange ? (selected) => onSelectionChange(selected, sequence.id) : undefined}
      >
        {cells}
      </ResourceTableRow>
    )
  }

  return {
    headers: headers,
    columns: columns,
    selectedColumns: selectedColumns,
    renderRow: renderRow,
  }
}

export default getSequenceTableRenderer
